
import type React from "react"
import { createContext, useContext, useState, useEffect, useCallback } from "react"

interface MobileContextType {
  isMobile: boolean
  isSidebarOpen: boolean
  openSidebar: () => void
  closeSidebar: () => void
  toggleSidebar: () => void
}

const MobileContext = createContext<MobileContextType | undefined>(undefined)

export function MobileProvider({ children }: { children: React.ReactNode }) {
  const [isMobile, setIsMobile] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Initial check
    checkMobile()

    // Add event listener
    window.addEventListener("resize", checkMobile)

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Close sidebar when switching to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsSidebarOpen(false)
    }
  }, [isMobile])

  const openSidebar = useCallback(() => {
    console.log("Opening sidebar")
    setIsSidebarOpen(true)
  }, [])

  const closeSidebar = useCallback(() => {
    console.log("Closing sidebar")
    setIsSidebarOpen(false)
  }, [])

  const toggleSidebar = useCallback(() => {
    console.log("Toggling sidebar, current state:", isSidebarOpen)
    setIsSidebarOpen((prev) => !prev)
  }, [isSidebarOpen])

  return (
    <MobileContext.Provider value={{ isMobile, isSidebarOpen, openSidebar, closeSidebar, toggleSidebar }}>
      {children}
    </MobileContext.Provider>
  )
}

export function useMobile() {
  const context = useContext(MobileContext)
  if (context === undefined) {
    throw new Error("useMobile must be used within a MobileProvider")
  }
  return context
}
